type CompletionStatus = 'incomplete' | 'completed';

interface ScormRelayPayload {
  completionStatus: CompletionStatus;
  progressPercent?: number;
  details?: string;
}

type ScormApi12 = {
  LMSInitialize?: (arg: string) => string;
  LMSSetValue?: (key: string, value: string) => string;
  LMSCommit?: (arg: string) => string;
  LMSFinish?: (arg: string) => string;
  LMSGetLastError?: () => string;
};

type ScormApi2004 = {
  Initialize?: (arg: string) => string;
  SetValue?: (key: string, value: string) => string;
  Commit?: (arg: string) => string;
  Terminate?: (arg: string) => string;
  GetLastError?: () => string;
};

const clampPercent = (value?: number): number | undefined => {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
};

/** Some LMS (e.g. 360Learning) treat progress_measure=0 as "started" (~50%). */
export const SCORM_INCOMPLETE_MIN_PROGRESS_PERCENT = 1;

export const normalizeScormProgressForLms = (
  completionStatus: CompletionStatus,
  progressPercent?: number
): number | undefined => {
  const clamped = clampPercent(progressPercent);
  if (completionStatus === 'completed') {
    return clamped;
  }
  if (clamped === undefined || clamped === 0) {
    return SCORM_INCOMPLETE_MIN_PROGRESS_PERCENT;
  }
  return clamped;
};

const getWindowCandidates = (): Window[] => {
  const candidates: Window[] = [];
  if (typeof window === 'undefined') {
    return candidates;
  }

  candidates.push(window);
  try {
    if (window.parent && window.parent !== window) {
      candidates.push(window.parent);
    }
  } catch {
    // Ignore cross-origin access issues.
  }
  try {
    if (window.top && window.top !== window && window.top !== window.parent) {
      candidates.push(window.top);
    }
  } catch {
    // Ignore cross-origin access issues.
  }

  return candidates;
};

const discoverScormApi12 = (): ScormApi12 | null => {
  for (const candidate of getWindowCandidates()) {
    try {
      const api = (candidate as unknown as { API?: ScormApi12 }).API;
      if (api) {
        return api;
      }
    } catch {
      // Ignore inaccessible candidate.
    }
  }
  return null;
};

const discoverScormApi2004 = (): ScormApi2004 | null => {
  for (const candidate of getWindowCandidates()) {
    try {
      const api = (candidate as unknown as { API_1484_11?: ScormApi2004 }).API_1484_11;
      if (api) {
        return api;
      }
    } catch {
      // Ignore inaccessible candidate.
    }
  }
  return null;
};

const ensureInitialized12 = (api: ScormApi12): void => {
  if (api.LMSInitialize) {
    const result = api.LMSInitialize('');
    if (result !== 'true' && result !== '1') {
      const error = api.LMSGetLastError?.() || 'unknown';
      throw new Error(`SCORM 1.2 LMSInitialize failed: ${error}`);
    }
  }
};

const ensureInitialized2004 = (api: ScormApi2004): void => {
  if (api.Initialize) {
    const result = api.Initialize('');
    if (result !== 'true' && result !== '1') {
      const error = api.GetLastError?.() || 'unknown';
      throw new Error(`SCORM 2004 Initialize failed: ${error}`);
    }
  }
};

const relayToScorm2004 = (api: ScormApi2004, payload: ScormRelayPayload): void => {
  ensureInitialized2004(api);

  const progressPercent = normalizeScormProgressForLms(
    payload.completionStatus,
    payload.progressPercent
  );
  const progressMeasure =
    progressPercent !== undefined ? (progressPercent / 100).toFixed(4) : '0.0100';
  const successStatus = payload.completionStatus === 'completed' ? 'passed' : 'unknown';

  api.SetValue?.('cmi.completion_status', payload.completionStatus);
  api.SetValue?.('cmi.progress_measure', progressMeasure);
  api.SetValue?.('cmi.success_status', successStatus);
  if (payload.details) {
    api.SetValue?.('cmi.location', payload.details.slice(0, 255));
  }
  api.Commit?.('');
};

const relayToScorm12 = (api: ScormApi12, payload: ScormRelayPayload): void => {
  ensureInitialized12(api);

  const lessonStatus = payload.completionStatus === 'completed' ? 'completed' : 'incomplete';

  api.LMSSetValue?.('cmi.core.lesson_status', lessonStatus);
  if (payload.details) {
    api.LMSSetValue?.('cmi.core.lesson_location', payload.details.slice(0, 255));
  }
  api.LMSCommit?.('');
};

const relayViaPostMessageBridge = (payload: ScormRelayPayload): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'FC_SCORM_RUNTIME_PROGRESS',
          payload,
        },
        '*'
      );
      return true;
    }
  } catch {
    // Ignore bridge failures and fall through silently.
  }

  return false;
};

export const relayScormRuntimeProgress = (payload: ScormRelayPayload): void => {
  const lmsPayload: ScormRelayPayload = {
    ...payload,
    progressPercent: normalizeScormProgressForLms(
      payload.completionStatus,
      payload.progressPercent
    ),
  };

  const api2004 = discoverScormApi2004();
  if (api2004) {
    relayToScorm2004(api2004, lmsPayload);
    return;
  }

  const api12 = discoverScormApi12();
  if (api12) {
    relayToScorm12(api12, lmsPayload);
    return;
  }

  relayViaPostMessageBridge(lmsPayload);
};

const terminateViaPostMessageBridge = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'FC_SCORM_RUNTIME_TERMINATE' }, '*');
      return true;
    }
  } catch {
    // Ignore bridge failures.
  }

  return false;
};

const terminateScorm2004 = (api: ScormApi2004): void => {
  api.Commit?.('');
  if (api.Terminate) {
    const result = api.Terminate('');
    if (result !== 'true' && result !== '1') {
      const error = api.GetLastError?.() || 'unknown';
      throw new Error(`SCORM 2004 Terminate failed: ${error}`);
    }
  }
};

const terminateScorm12 = (api: ScormApi12): void => {
  api.LMSCommit?.('');
  if (api.LMSFinish) {
    const result = api.LMSFinish('');
    if (result !== 'true' && result !== '1') {
      const error = api.LMSGetLastError?.() || 'unknown';
      throw new Error(`SCORM 1.2 LMSFinish failed: ${error}`);
    }
  }
};

export const terminateScormRuntime = (): void => {
  const api2004 = discoverScormApi2004();
  if (api2004) {
    try {
      terminateScorm2004(api2004);
    } catch {
      // Best-effort on unload.
    }
    return;
  }

  const api12 = discoverScormApi12();
  if (api12) {
    try {
      terminateScorm12(api12);
    } catch {
      // Best-effort on unload.
    }
    return;
  }

  terminateViaPostMessageBridge();
};
